/**
 * Authentication Store
 *
 * Zustand store for managing authentication state.
 * Handles login, logout, registration, and session management.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// User type
export interface User {
  id: string;
  email: string;
  role: 'superadmin' | 'admin' | 'user';
  displayName?: string | null;
  emailVerified: boolean;
  mfaEnabled: boolean;
  mfaMethod?: string | null;
  timezone?: string;
  locale?: string;
  createdAt: string;
  lastLoginAt?: string | null;
  quotas?: {
    maxForms: number;
    currentFormCount: number;
    maxStorageBytes: number;
    currentStorageBytes: number;
  };
}

// Login credentials
export interface LoginCredentials {
  email: string;
  password: string;
  mfaCode?: string;
  mfaMethod?: 'totp' | 'email' | 'backup';
}

// Registration data
export interface RegisterData {
  email: string;
  password: string;
  displayName?: string;
  inviteToken?: string;
  locale?: string; // User's preferred language from the site
}

// Login result
export interface LoginResult {
  success: boolean;
  requiresMFA?: boolean;
  mfaMethod?: string;
  mfaSessionToken?: string;
  error?: string;
}

// Auth state
interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // MFA challenge state
  mfaRequired: boolean;
  mfaMethod: string | null;
  mfaSessionToken: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  refreshToken: () => Promise<boolean>;
  checkAuth: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  clearError: () => void;
  setMFAChallenge: (method: string, sessionToken: string) => void;
  clearMFAChallenge: () => void;
}

// API base URL
const API_BASE = '/api';

// Helper for API calls
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ data?: T; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include cookies
    });

    const data = await response.json() as T & { error?: string };

    if (!response.ok) {
      return {
        error: data.error || `Request failed with status ${response.status}`,
      };
    }

    return { data };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

// Create the store
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
      mfaRequired: false,
      mfaMethod: null,
      mfaSessionToken: null,

      // Login
      login: async (credentials: LoginCredentials): Promise<LoginResult> => {
        set({ isLoading: true, error: null });

        try {
          const { data, error } = await fetchAPI<{
            success: boolean;
            user?: User;
            accessToken?: string;
            requiresMFA?: boolean;
            mfaMethod?: string;
            mfaSessionToken?: string;
          }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
          });

          if (error) {
            set({ isLoading: false, error });
            return { success: false, error };
          }

          if (data?.requiresMFA) {
            set({
              isLoading: false,
              mfaRequired: true,
              mfaMethod: data.mfaMethod || null,
              mfaSessionToken: data.mfaSessionToken || null,
            });
            return {
              success: false,
              requiresMFA: true,
              mfaMethod: data.mfaMethod,
              mfaSessionToken: data.mfaSessionToken,
            };
          }

          if (data?.success && data.user) {
            set({
              user: data.user,
              accessToken: data.accessToken || null,
              isLoading: false,
              isAuthenticated: true,
              error: null,
              mfaRequired: false,
              mfaMethod: null,
              mfaSessionToken: null,
            });
            return { success: true };
          }

          set({ isLoading: false, error: 'Login failed' });
          return { success: false, error: 'Login failed' };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          set({ isLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // Logout
      logout: async (): Promise<void> => {
        set({ isLoading: true });

        try {
          await fetchAPI('/auth/logout', { method: 'POST' });
        } catch (error) {
          console.error('Logout error:', error);
        }

        set({
          user: null,
          accessToken: null,
          isLoading: false,
          isAuthenticated: false,
          error: null,
          mfaRequired: false,
          mfaMethod: null,
          mfaSessionToken: null,
        });
      },

      // Register
      register: async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
        set({ isLoading: true, error: null });

        try {
          const { data: response, error } = await fetchAPI<{
            success: boolean;
            user: User;
            accessToken: string;
            requiresEmailVerification: boolean;
          }>('/auth/register', {
            method: 'POST',
            body: JSON.stringify(data),
          });

          if (error) {
            set({ isLoading: false, error });
            return { success: false, error };
          }

          if (response?.success && response.user) {
            set({
              user: response.user,
              accessToken: response.accessToken,
              isLoading: false,
              isAuthenticated: true,
              error: null,
            });
            return { success: true };
          }

          set({ isLoading: false, error: 'Registration failed' });
          return { success: false, error: 'Registration failed' };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Registration failed';
          set({ isLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // Refresh token
      refreshToken: async (): Promise<boolean> => {
        try {
          const { data, error } = await fetchAPI<{
            success: boolean;
            accessToken: string;
            user: User;
          }>('/auth/refresh', {
            method: 'POST',
          });

          if (error || !data?.success) {
            set({
              user: null,
              accessToken: null,
              isAuthenticated: false,
            });
            return false;
          }

          set({
            accessToken: data.accessToken,
            user: data.user,
            isAuthenticated: true,
          });
          return true;
        } catch (error) {
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
          });
          return false;
        }
      },

      // Check current auth status
      checkAuth: async (): Promise<void> => {
        set({ isLoading: true });

        try {
          const { data, error } = await fetchAPI<User>('/auth/me');

          if (error) {
            // Try to refresh token
            const refreshed = await get().refreshToken();
            if (!refreshed) {
              set({
                user: null,
                accessToken: null,
                isLoading: false,
                isAuthenticated: false,
              });
              return;
            }

            // Retry getting user info
            const { data: userData } = await fetchAPI<User>('/auth/me');
            if (userData) {
              set({
                user: userData,
                isLoading: false,
                isAuthenticated: true,
              });
              return;
            }
          }

          if (data) {
            set({
              user: data,
              isLoading: false,
              isAuthenticated: true,
            });
          } else {
            set({
              user: null,
              accessToken: null,
              isLoading: false,
              isAuthenticated: false,
            });
          }
        } catch (error) {
          set({
            user: null,
            accessToken: null,
            isLoading: false,
            isAuthenticated: false,
          });
        }
      },

      // Update user
      updateUser: (updates: Partial<User>): void => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },

      // Clear error
      clearError: (): void => {
        set({ error: null });
      },

      // Set MFA challenge
      setMFAChallenge: (method: string, sessionToken: string): void => {
        set({
          mfaRequired: true,
          mfaMethod: method,
          mfaSessionToken: sessionToken,
        });
      },

      // Clear MFA challenge
      clearMFAChallenge: (): void => {
        set({
          mfaRequired: false,
          mfaMethod: null,
          mfaSessionToken: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist non-sensitive data
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Selector hooks for common use cases
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useIsSuperadmin = () =>
  useAuthStore((state) => state.user?.role === 'superadmin');
export const useIsAdmin = () =>
  useAuthStore((state) => state.user?.role === 'superadmin' || state.user?.role === 'admin');
